<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import SiteAdminService from '@/services/SiteAdminService'
import { useSiteDraft } from '@/composables/useSiteDraft'
import Spinner from '@/components/ui/Spinner.vue'
import Alert from '@/components/Atoms/Alert.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import BroadcastRegistrationFieldsEditor from '@/components/Sites/Broadcast/BroadcastRegistrationFieldsEditor.vue'
import { trackSites, SITES_EVENTS } from '@/lib/tracking/sites'
import { isBroadcast as isBroadcastSite } from '@/components/Sites/siteTemplates'
import { ACCESS_LABELS, normalizeAccess } from '@/Utils/siteAccess'
import {
	AUDIENCE_PRESETS, normalizeAudience, NAME_FORMATS, REGISTRATION_CONSENT_TEXT_MAX,
	fieldsFromSettings, fieldsToRuntime, fieldErrors, consentUrlError,
} from '@/components/Sites/broadcastAudience'

// Settings › Audience (site-admin API v0.19, broadcast template): the label
// preset and the register-to-watch form. Settings go to the runtime draft
// like Theme; "Require registration" also sets the main live item's access
// (Public ↔ Login required) right away, because access lives on the item.
// A paid live item is never changed here — viewers keep buying access.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		required: true,
	},
})
const { notify } = useNotification()
const draft = useSiteDraft(() => props.siteId)
const isBroadcast = computed(() => isBroadcastSite(props.site))

const loading = ref(true)
const loadError = ref(null)
const saving = ref(false)
const settings = ref({})
const writableKeys = ref(null)
const liveItems = ref([])

const fromSettings = (s) => ({
	broadcastAudience: normalizeAudience(s.broadcastAudience),
	broadcastRegistrationGate: s.broadcastRegistrationGate === true,
	registrationNameFormat: s.registrationNameFormat === 'split' ? 'split' : 'full',
	registrationFields: fieldsFromSettings(s.registrationFields),
	registrationConsentText: s.registrationConsentText || '',
	registrationConsentUrl: s.registrationConsentUrl || '',
	registrationEmailVerification: s.registrationEmailVerification === true,
})
const form = ref(fromSettings({}))
const base = ref(fromSettings({}))
const fillForm = () => {
	base.value = fromSettings(settings.value)
	form.value = JSON.parse(JSON.stringify(base.value))
}

const writable = (key) => !Array.isArray(writableKeys.value) || writableKeys.value.includes(key)
const supported = computed(() => writable('broadcastAudience'))
const gateSupported = computed(() => writable('broadcastRegistrationGate'))

const load = async () => {
	try {
		const [res, liveRes] = await Promise.all([
			SiteAdminService.getSettings(props.siteId),
			SiteAdminService.listContent(props.siteId, { type: 'live' }).catch(() => ({ items: [] })),
		])
		settings.value = { ...(res?.settings || {}), ...(res?.draft && typeof res.draft === 'object' ? res.draft : {}) }
		writableKeys.value = Array.isArray(res?.writableKeys) ? res.writableKeys : null
		liveItems.value = (liveRes?.items || []).filter((i) => i.type === 'live')
		draft.applySettingsResponse(res)
		if (typeof res?.draftCount !== 'number' && !res?.draft) draft.state.value.draftCount = null
		fillForm()
		loadError.value = null
	} catch (err) {
		loadError.value = err
	}
}
onMounted(async () => { loading.value = true; await load(); loading.value = false })

// The item in the big player: Settings › Broadcast's pick, else the first published live item.
const liveItem = computed(() => {
	const id = Number(settings.value.broadcastLiveItemId)
	if (id) return liveItems.value.find((i) => Number(i.id) === id) || null
	return liveItems.value.find((i) => i.published !== false) || liveItems.value[0] || null
})
const liveAccess = computed(() => (liveItem.value ? normalizeAccess(liveItem.value.access) : null))
const livePaid = computed(() => liveAccess.value === 'paid')

// --- validation --------------------------------------------------------------------
const rowErrors = computed(() => fieldErrors(form.value.registrationFields))
const consentUrlErr = computed(() => consentUrlError(form.value.registrationConsentUrl))
const consentTextErr = computed(() => {
	const t = String(form.value.registrationConsentText || '').trim()
	if (t.length > REGISTRATION_CONSENT_TEXT_MAX) return `Up to ${REGISTRATION_CONSENT_TEXT_MAX} characters`
	if (!t && String(form.value.registrationConsentUrl || '').trim()) return 'Add the sentence viewers agree to'
	return null
})
const valid = computed(() => !rowErrors.value.some(Boolean) && !consentUrlErr.value && !consentTextErr.value)

// --- changes -----------------------------------------------------------------------
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b)
const toRuntime = (key, value) => {
	if (key === 'registrationFields') return fieldsToRuntime(value)
	if (key === 'registrationConsentText' || key === 'registrationConsentUrl') return String(value || '').trim() || null
	return value
}
const patch = computed(() => {
	const p = {}
	for (const key of Object.keys(form.value)) {
		if (!writable(key)) continue
		if (!same(form.value[key], base.value[key])) p[key] = toRuntime(key, form.value[key])
	}
	// The gate signs viewers up itself, but the site's own Register page should agree.
	if (p.broadcastRegistrationGate === true && settings.value.registrationEnabled === false && writable('registrationEnabled')) p.registrationEnabled = true
	return p
})
// What happens to the live item's access on save.
const accessChange = computed(() => {
	if (!liveItem.value || livePaid.value) return null
	if (form.value.broadcastRegistrationGate && liveAccess.value === 'public') return 'registered'
	if (!form.value.broadcastRegistrationGate && base.value.broadcastRegistrationGate && liveAccess.value === 'registered') return 'public'
	return null
})
const unsavedCount = computed(() => Object.keys(patch.value).length + (accessChange.value ? 1 : 0))

const draftSupported = computed(() => draft.draftSupported.value)
const unpublishedCount = computed(() => draft.draftCount.value)
const publishing = computed(() => draft.state.value.publishing)
const saveLabel = computed(() => (draftSupported.value ? 'Save draft' : 'Save settings'))

const save = async () => {
	if (!valid.value || saving.value || !unsavedCount.value) return
	saving.value = true
	const body = { ...patch.value }
	const access = accessChange.value
	try {
		if (Object.keys(body).length) {
			const res = await SiteAdminService.updateSettings(props.siteId, body, { target: 'draft' })
			settings.value = { ...settings.value, ...body }
			if (Array.isArray(res?.writableKeys)) writableKeys.value = res.writableKeys
			draft.noteDraftSaved(res)
		}
		if (access) {
			const res = await SiteAdminService.updateContent(props.siteId, liveItem.value.id, { access })
			const updated = { ...liveItem.value, access, ...(res?.item || {}) }
			liveItems.value = liveItems.value.map((i) => (Number(i.id) === Number(updated.id) ? updated : i))
		}
		if ('broadcastAudience' in body) trackSites(SITES_EVENTS.AUDIENCE_PRESET_SET, props.site, { preset: body.broadcastAudience, surface: 'settings' })
		if ('registrationFields' in body || 'broadcastRegistrationGate' in body) {
			trackSites(SITES_EVENTS.REGISTRATION_FIELDS_UPDATED, props.site, { count: form.value.registrationFields.length, gate: form.value.broadcastRegistrationGate })
		}
		fillForm()
		const parts = []
		if (Object.keys(body).length) parts.push(draftSupported.value ? 'Saved to the draft — publish to show it on the site.' : 'Settings saved.')
		if (access) parts.push(`“${liveItem.value.title}” is now ${ACCESS_LABELS[access]}.`)
		notify({ group: 'success', text: parts.join(' ') })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not save the audience settings.' })
	}
	saving.value = false
}
const publish = () => draft.publish().catch(() => null)
const discardUnsaved = () => fillForm()

const beforeUnload = (e) => { if (unsavedCount.value) { e.preventDefault(); e.returnValue = '' } }
onMounted(() => window.addEventListener('beforeunload', beforeUnload))
onBeforeUnmount(() => window.removeEventListener('beforeunload', beforeUnload))
</script>

<template>
	<div data-testid="site-audience">
		<div class="mb-4">
			<h2 class="mb-0.5 text-headline font-semibold text-surface-text-2">Audience</h2>
			<p class="mb-0 text-tiny text-surface-7">
				Who the site is for and how viewers get in.
				<template v-if="draftSupported">Changes are saved as a draft and go live when you publish.</template>
				<template v-else>Saved changes go live within a minute.</template>
			</p>
		</div>

		<Alert v-if="!isBroadcast" type="normal" class="mb-4" data-testid="audience-not-active">
			This site uses the Video library template. These settings apply once you switch to Broadcast under
			<router-link :to="{ name: 'SiteGeneral', params: { siteId } }" class="underline">Settings › General</router-link>.
		</Alert>

		<Alert v-if="loadError" type="error" class="mb-4">{{ loadError.message }}</Alert>
		<Spinner v-else-if="loading" text="Loading settings…" classes="text-surface-8 mt-24" spinner-color="var(--c-dark-8)" spinner-size="15px" />

		<template v-else>
			<p v-if="!supported" class="mb-4 rounded-md bg-surface-3 px-3 py-2 text-tiny text-surface-7" data-testid="audience-unsupported">
				Audience settings need a newer site runtime — they appear here as soon as your site is updated.
			</p>

			<div class="flex max-w-3xl flex-col gap-4" data-testid="audience-form">
				<!-- Preset -->
				<section class="rounded-lg bg-surface-2 p-4 sm:p-5" data-testid="audience-preset">
					<h3 class="mb-1 text-sm font-semibold text-white">What are you streaming?</h3>
					<p class="mb-3 text-tiny text-surface-7">Sets the wording viewers see — “Next session” or “Next service”, “Agenda” or “Service times”, “Donate” or “Give”. Fine-tune any label under <router-link :to="{ name: 'SiteTheme', params: { siteId, section: 'texts' } }" class="underline" data-testid="audience-texts-link">Theme › Texts</router-link>.</p>
					<div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
						<label v-for="p in AUDIENCE_PRESETS" :key="p.id"
							class="flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 transition-colors"
							:class="form.broadcastAudience === p.id ? 'border-main bg-main-transparent' : 'border-surface-4 bg-surface-3 hover:border-surface-6'"
							:data-testid="`audience-preset-${p.id}`" :data-active="form.broadcastAudience === p.id">
							<input v-model="form.broadcastAudience" type="radio" name="audience-preset" :value="p.id" class="mt-1 size-3.5 shrink-0 accent-main" :disabled="!supported">
							<span class="flex min-w-0 flex-col gap-0.5">
								<span class="text-sm font-medium text-white">{{ p.title }}</span>
								<span class="text-tiny text-surface-7">{{ p.description }}</span>
							</span>
						</label>
					</div>
				</section>

				<!-- Register to watch -->
				<section v-if="gateSupported" class="rounded-lg bg-surface-2 p-4 sm:p-5" data-testid="audience-registration">
					<h3 class="mb-1 text-sm font-semibold text-white">Register to watch</h3>
					<p class="mb-3 text-tiny text-surface-7">Viewers fill in a short form right where the player is and start watching at once — no password, no extra page. Returning viewers sign in from the same panel with a code sent to their email.</p>

					<div class="rounded-lg bg-surface-3 px-3 py-2.5">
						<FormSwitch id="audience-gate" v-model="form.broadcastRegistrationGate" label="Require registration to watch the live stream" label-position="left" label-styles="w-full justify-between gap-3 text-sm text-surface-text-1" :disabled="!supported || livePaid" data-testid="audience-gate-switch" />
						<p class="mb-0 mt-1 pr-14 text-tiny text-surface-7" data-testid="audience-gate-hint">
							<template v-if="!liveItem">Add a live stream under Content › Library first — the form appears on the main live stream.</template>
							<template v-else-if="livePaid">“{{ liveItem.title }}” is paid: viewers buy access instead. Make it Public or Login required in the Library to use the form.</template>
							<template v-else-if="accessChange === 'registered'">On save, “{{ liveItem.title }}” changes from Public to Login required.</template>
							<template v-else-if="accessChange === 'public'">On save, “{{ liveItem.title }}” goes back to Public — anyone can watch.</template>
							<template v-else>Main live stream: “{{ liveItem.title }}” ({{ ACCESS_LABELS[liveAccess] }}).</template>
						</p>
					</div>

					<div v-if="form.broadcastRegistrationGate" class="mt-4 flex flex-col gap-4" data-testid="audience-registration-options">
						<div class="audience-field min-w-0 sm:max-w-xs">
							<FormSelect id="audience-name-format" v-model="form.registrationNameFormat" label="Name" :options="NAME_FORMATS" :disabled="!supported" />
						</div>

						<div class="min-w-0">
							<p class="mb-1 text-tiny font-medium text-surface-text-1">Extra questions</p>
							<BroadcastRegistrationFieldsEditor v-model="form.registrationFields" :saved-keys="base.registrationFields.map((f) => f.key)" :errors="rowErrors" :disabled="!supported" />
						</div>

						<div class="audience-field flex min-w-0 flex-col gap-2" data-testid="audience-consent">
							<FormInput id="audience-consent-text" v-model="form.registrationConsentText" label="Consent checkbox (optional)" placeholder="I agree to the [privacy policy] and to receive event updates." input-classes="placeholder:text-surface-6" :maxlength="REGISTRATION_CONSENT_TEXT_MAX" :is-error="Boolean(consentTextErr)" :disabled="!supported" />
							<FormInput id="audience-consent-url" v-model="form.registrationConsentUrl" label="Consent link (optional)" placeholder="https://… or /privacy" input-classes="placeholder:text-surface-6" :is-error="Boolean(consentUrlErr)" :disabled="!supported" />
							<p class="mb-0 text-tiny" :class="consentTextErr || consentUrlErr ? 'text-error' : 'text-surface-7'">
								{{ consentTextErr || consentUrlErr || 'Viewers must tick it to register. Put [square brackets] around the words that should link; without them a “Learn more” link is added.' }}
							</p>
						</div>

						<div class="rounded-lg bg-surface-3 px-3 py-2.5">
							<FormSwitch id="audience-verify" v-model="form.registrationEmailVerification" label="Verify email before watching" label-position="left" label-styles="w-full justify-between gap-3 text-sm text-surface-text-1" :disabled="!supported" data-testid="audience-verify-switch" />
							<p class="mb-0 mt-1 pr-14 text-tiny text-surface-7">Off: viewers watch as soon as they register. On: they first type a 6-digit code we email them — fewer fake addresses, one more step.</p>
						</div>

						<p class="mb-0 text-tiny text-surface-7">Answers appear in <router-link :to="{ name: 'SiteViewers', params: { siteId } }" class="underline">Audience › Viewers</router-link> and in its CSV export, one column per question.</p>
					</div>
				</section>

				<div class="sticky bottom-0 z-10 -mx-4 flex flex-wrap items-center gap-3 border-t border-surface-3 bg-surface-1/95 px-4 py-3 backdrop-blur lg:mx-0 lg:rounded-lg lg:border lg:px-5" data-testid="audience-save-bar">
					<p class="mb-0 mr-auto flex min-w-0 flex-wrap items-center gap-x-2 text-tiny">
						<template v-if="unsavedCount">
							<span class="size-2 shrink-0 rounded-full bg-gold" aria-hidden="true"></span>
							<span class="text-white">{{ unsavedCount }} unsaved {{ unsavedCount === 1 ? 'change' : 'changes' }}</span>
							<button type="button" class="text-surface-8 underline hover:text-white" data-testid="audience-discard" @click="discardUnsaved">Discard</button>
						</template>
						<template v-else-if="draftSupported && unpublishedCount">
							<span class="size-2 shrink-0 rounded-full bg-main" aria-hidden="true"></span>
							<span class="text-white">{{ unpublishedCount }} unpublished {{ unpublishedCount === 1 ? 'change' : 'changes' }}</span>
							<span class="text-surface-7">— viewers still see the published version.</span>
						</template>
						<template v-else>
							<span class="size-2 shrink-0 rounded-full bg-success" aria-hidden="true"></span>
							<span class="text-surface-7">{{ draftSupported ? 'Everything is published.' : 'All changes saved.' }}</span>
						</template>
					</p>
					<FormButton v-if="!unsavedCount && draftSupported && unpublishedCount" type="main" size="md" class="w-full justify-center sm:w-auto" :is-loading="publishing" :disabled="publishing" data-testid="audience-publish" @click="publish">
						Publish changes
					</FormButton>
					<FormButton v-else type="main" size="md" class="w-full justify-center sm:w-auto" :disabled="!valid || saving || !unsavedCount || !supported" :is-loading="saving" data-testid="audience-save" @click="save">
						{{ saveLabel }}
					</FormButton>
				</div>
			</div>
		</template>
	</div>
</template>

<style scoped>
.audience-field :deep(input),
.audience-field :deep(select) {
	border: 1px solid var(--surface-5);
}
</style>
